// src/experiments/questionFlags.js
//
// Changes to a question flow switched by a feature flag (flags.config.js)
// rather than split by an experiment. The flow stays one directory: its
// question JSON marks what a flag changes, and the question engine applies
// those marks to every question it loads, before anything renders or
// validates it.
//
//   "skipWhenFlag": "<flag>"   on a question: skipped while the flag is on,
//                              forwards and back, the way `skipIfPopulated`
//                              skips.
//   "hideWhenFlag": "<flag>"   on a field: left out of the question while the
//                              flag is on, so it neither renders nor validates.
//
// Question numbers never change, so `branching.questionIndex`, the stepper's
// edit links and the step URLs mean the same thing whichever way a flag is
// set, and turning a flag off mid-journey costs the visitor nothing they have
// answered. Each flag declares where it applies in FLAGGED_FLOW_CHANGES. An
// answer a flag stops the flow asking for is defaulted where the API request
// is built, not here.

import { getFlag } from './flags';

export function isFlagOn(key) {
  return getFlag(key) === true;
}

// flag -> where it applies, and what turning it on owes the rest of the app.
//   flowSlugs      the journeys, by URL slug, whose marks the flag applies.
//                  A sub-flow runs at the slug of the flow that switched into
//                  it, so this covers them; another journey that loads the
//                  same question files at its own slug is left alone.
//   products       the engine `product`s whose flows the flag changes, with
//                  every sub-flow the journey can end in
//   hiddenFilters  results-page filter sections that refine a dropped answer
export const FLAGGED_FLOW_CHANGES = {
  // CarInsurance/Question5.json ("What is the … mainly used for?") is skipped
  // and the field is hidden on the returning user's "Confirm the details of
  // the vehicle" (ReturningUser/Question3.json). The quote request defaults an
  // unanswered vehicle use to personal (mapFormDataToCarQuoteApiStructure);
  // a returning user's own answer still comes over with the vehicle they
  // picked. AdditionalDriver is the sub-flow the car journey can finish in.
  // No vehicle-use question means no excess expectation to filter against.
  // The combo journey (online-insurance-quote) asks the car questions too and
  // keeps the vehicle-use question.
  car_lean_flow: {
    flowSlugs: ['car-insurance-quote'],
    products: ['CarInsurance', 'ReturningUser', 'AdditionalDriver'],
    hiddenFilters: ['Excess'],
  },
};

// The flags in effect on the journey at `flowSlug`, as the `isOn` the
// functions below take: a flag that lists `flowSlugs` is off on any other.
export function flagsOnForFlow(
  flowSlug,
  isOn = isFlagOn,
  changes = FLAGGED_FLOW_CHANGES
) {
  return (key) => {
    const slugs = changes[key]?.flowSlugs;
    if (Array.isArray(slugs) && !slugs.includes(flowSlug)) return false;
    return isOn(key);
  };
}

const NOT_FIELDS = new Set(['layout', 'columns']);

const isFlagKey = (value) => typeof value === 'string' && value !== '';

export function isQuestionSkippedByFlag(questionJson, isOn = isFlagOn) {
  const key = questionJson?.skipWhenFlag;
  return isFlagKey(key) && isOn(key);
}

// The question as this visitor sees it: fields a flag hides left out. Returns
// the same object when nothing in it changes, so a question with no marks
// costs nothing.
export function applyQuestionFlags(questionJson, isOn = isFlagOn) {
  if (!Array.isArray(questionJson?.questions)) return questionJson;

  let changed = false;
  const questions = questionJson.questions.map((group) => {
    if (!group || typeof group !== 'object') return group;
    let groupChanged = false;
    const out = {};
    for (const [key, field] of Object.entries(group)) {
      if (
        !NOT_FIELDS.has(key) &&
        isFlagKey(field?.hideWhenFlag) &&
        isOn(field.hideWhenFlag)
      ) {
        groupChanged = true;
        continue;
      }
      out[key] = field;
    }
    if (!groupChanged) return group;
    changed = true;
    return out;
  });

  return changed ? { ...questionJson, questions } : questionJson;
}

function changesFor(product, isOn, changes) {
  if (!product) return [];
  return Object.entries(changes)
    .filter(([key, change]) => change.products.includes(product) && isOn(key))
    .map(([, change]) => change);
}

// The filter sections to leave out of a results page showing answers that
// `product`'s flow collected. Empty for a product no flag that is on changes.
export function hiddenFiltersForProduct(
  product,
  isOn = isFlagOn,
  changes = FLAGGED_FLOW_CHANGES
) {
  const keys = [];
  for (const change of changesFor(product, isOn, changes)) {
    for (const key of change.hiddenFilters || []) {
      if (!keys.includes(key)) keys.push(key);
    }
  }
  return keys;
}

// A copy of a filter schema without the named sections.
export function withoutFilterSections(schema, keys) {
  if (!schema || !keys || !keys.length) return schema;
  return Object.fromEntries(
    Object.entries(schema).filter(([key]) => !keys.includes(key))
  );
}
